/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { View } from '@az/starc-ui';
import cx from 'classnames';
import { Legal } from '@/components/AZCustomComponent/Legal';
import { mapContentstackLegalValues } from '@/utils/mapContentstackLegalValues';
import {
  type ContentStackBlogPostSummary,
  type ContentStackPostReviewer,
  type ContentStackBlogtypeGrid,
} from '../../interface';
import styles from './CMSBlogGrid.module.scss';
import { CMSHeadline } from '../CMSHeadline/CMSHeadline';
import { CMSImageContentCard } from '../CMSImageContentCard/CMSImageContentCard';
import { useEffect, useRef, useState } from 'react';
import { isLegalDefined } from '../../utils/isLegalDefined';

type Props = {
  content: ContentStackBlogtypeGrid;
  reviewers?: Array<{
    blogReviewer: Pick<ContentStackPostReviewer, 'title' | 'post_review' | 'url'> | undefined;
    date: ContentStackBlogPostSummary['reviewed_date'] | undefined;
  }>;
};

export const CMSBlogGrid = ({ content, reviewers }: Props) => {
  const { headline, blog_content, background_color, legal, mobile_horizontal_scroll_disabled } =
    content;
  const isHorizontalScrollEnabled = !mobile_horizontal_scroll_disabled;
  const imgRefs = useRef<Array<HTMLElement | null>>([]);
  const [imageHeight, setImageHeight] = useState(0);

  const findHighestImage = () => {
    if (imgRefs.current.length) {
      const highestImageSize = imgRefs.current.reduce(
        (maxHeight, imgHeight) => {
          const imageHeight = imgHeight?.clientHeight ?? 0;

          return maxHeight > imageHeight ? maxHeight : imageHeight;
        },

        0,
      );

      setImageHeight(highestImageSize);
    }
  };

  useEffect(() => {
    findHighestImage();
    window.addEventListener('resize', findHighestImage);
    imgRefs.current.forEach((imgRef) => imgRef?.addEventListener('load', findHighestImage));
    const imageReferences = imgRefs.current;

    return () => {
      imageReferences.forEach((imgRef) => imgRef?.removeEventListener('load', findHighestImage));
      window.removeEventListener('resize', findHighestImage);
    };
  }, [imgRefs]);

  return (
    <View className={styles.outerContainer}>
      <CMSHeadline className={styles.headline} fullWidth={true}>
        {headline}
      </CMSHeadline>
      <View className={styles.container}>
        <View
          className={cx(
            styles.blogContent,
            isHorizontalScrollEnabled && styles.blogContentScrollable,
          )}
        >
          <View
            className={cx(
              styles.mobileContainer,
              isHorizontalScrollEnabled && styles.mobileContainerScrollable,
            )}
          >
            {blog_content?.map((content, index) => (
              <CMSImageContentCard
                key={index}
                {...content}
                review={reviewers?.[index]}
                withBackground={background_color}
                isHorizontalScrollEnabled={isHorizontalScrollEnabled}
                ref={(img: HTMLImageElement) => {
                  imgRefs.current[index] = img;
                }}
              />
            ))}
          </View>
        </View>

        {background_color && (
          <View
            className={styles.background}
            attributes={{ style: { '--bg-height': `${imageHeight / 2}px` } }}
          />
        )}
      </View>
      {legal && isLegalDefined(legal) && (
        <View
          className={cx(
            styles.legalContainer,
            background_color && styles.legalContainerWithBackground,
          )}
        >
          <Legal content={mapContentstackLegalValues(legal)} overrideClassName={styles.legalText} />
        </View>
      )}
    </View>
  );
};
